///  'FORECAST TASKS - IN PROGRESS' DATASOURCE  ///
// some config from forecast-tasks.model.ts also applies here

import { DateUtilsService } from 'src/app/shared/services/date-utils.service'
import { NotificationService } from 'src/app/shared/services/notification.service'
import { ChartConfig, ChartField } from '../chart.model'
import { GROUP_KEYS_FUNCTION, WarningNotifier } from '../constants'
import { FORECAST_TASKS_FILTER_FIELDS, FORECAST_TASKS_DATE_FILTER_FIELDS, FORECAST_TASKS_VALUE_FIELDS } from './forecast-tasks.model'
import { RecalcCompletionForecast } from '../dto/recalc-completion-forecast.dto'
import { DateRangeType } from '../enums'

export const FORECAST_TASKS_IN_PROGRESS_ARGUMENT_FIELDS: ChartField[] = [
  {
    dataField: 'forecastStartDate',
    displayName: 'Forecast Start Date',
    dataType: 'date',
  }
]

export const FORECAST_TASKS_IN_PROGRESS_DATE_FILTER_FIELDS: ChartField[] = FORECAST_TASKS_DATE_FILTER_FIELDS

export const FORECAST_TASKS_IN_PROGRESS_FILTER_FIELDS: ChartField[] = FORECAST_TASKS_FILTER_FIELDS

export const FORECAST_TASKS_IN_PROGRESS_VALUE_FIELDS: ChartField[] = FORECAST_TASKS_VALUE_FIELDS

type ForecastIntervalWarningType =
  | 'missingTask1Start'
  | 'missingTask1End'
  | 'missingTask2'
  | 'missingTask2End'
  | 'startAfterEnd'

const FORECAST_INTERVAL_WARNING_TEXT: Record<ForecastIntervalWarningType, string> = {
  missingTask1Start: 'No start date was found for task 1.',
  missingTask1End: 'No completion date was found for task 1.',
  missingTask2: 'Task 2 was not found after filtering. Check any custom filters.',
  missingTask2End: 'No selected end date was found for task 2.',
  startAfterEnd: 'The task 1 date is after the task 2 date.',
}

export class ForecastIntervalWarningCollector implements WarningNotifier {
  private warnings = new Map<ForecastIntervalWarningType, Set<string>>()

  constructor(
    private notificationService: NotificationService,
    private cardTitle: string
  ) {}

  report(type: ForecastIntervalWarningType, jobNumber: string): void {
    const jobs = this.warnings.get(type) ?? new Set<string>()
    jobs.add(jobNumber)
    this.warnings.set(type, jobs)
  }

  showWarning(message: string, title?: string): void {
    this.notificationService.showWarning(message, title)
  }

  showWarningPermanent(message: string, title?: string): void {
    this.notificationService.showWarningPermanent(message, title)
  }

  flush(): void {
    for (const [type, jobs] of this.warnings) {
      const jobList = Array.from(jobs).sort().join(', ')
      const message = `Chart '${this.cardTitle}': ${FORECAST_INTERVAL_WARNING_TEXT[type]} Ignoring jobs: ${jobList}.`
      if (type === 'startAfterEnd') {
        this.notificationService.showWarning(message)
      } else {
        this.notificationService.showWarningPermanent(message)
      }
    }
  }
}

function reportForecastIntervalWarning(
  notifier: WarningNotifier,
  type: ForecastIntervalWarningType,
  jobNumber: string,
  message: string,
  permanent: boolean
): void {
  if (notifier instanceof ForecastIntervalWarningCollector) {
    notifier.report(type, jobNumber)
  } else if (permanent) {
    notifier.showWarningPermanent(message)
  } else {
    notifier.showWarning(message)
  }
}

export function getForecastTaskInProgressInterval(
  task2Items: RecalcCompletionForecast[],
  task1Item: RecalcCompletionForecast,
  config: ChartConfig,
  notificationService: WarningNotifier
): [Date, Date] | undefined {
  if (!config) throw Error('forecast task in progress group func requires config object')

  const prefix = `Chart '${config.cardTitle}': `
  const suffix = `. Ignoring job.`

  const startField = config.taskMaster2Id
    ? config.task1DateField ?? 'forecastStartDate'
    : 'forecastStartDate'
  const startValue = task1Item[startField as keyof RecalcCompletionForecast]
  let start = startValue == null ? new Date(Number.NaN) : new Date(startValue)
  if (Number.isNaN(start.getTime())) {
    reportForecastIntervalWarning(
      notificationService,
      'missingTask1Start',
      task1Item.jobNumber,
      `${prefix}No start date for task 1 (${task1Item.taskTitle}) for job ${task1Item.jobNumber}${suffix}`,
      true
    )
    return undefined
  }

  let end = new Date()
  if (!config.taskMaster2Id) {
    // (only looking for single task type)

    end = new Date(task1Item.forecastCompletionDate)
    if (Number.isNaN(end.getTime())) {
      reportForecastIntervalWarning(
        notificationService,
        'missingTask1End',
        task1Item.jobNumber,
        `${prefix}No end date for task 1 (${task1Item.taskTitle}) for job ${task1Item.jobNumber}${suffix}`,
        true
      )
      return undefined
    }
  } else {
    // (finding duration between two tasks)

    // should only be one of each task type per job
    const task2 = task2Items.find(
      (i) => i.taskMasterId === config.taskMaster2Id && i.jobId === task1Item.jobId
    )

    if (!task2) {
      reportForecastIntervalWarning(
        notificationService,
        'missingTask2',
        task1Item.jobNumber,
        `${prefix}Second task not found for job ${task1Item.jobNumber} after filtering. Check any custom filters ${suffix}`,
        true
      )
      return undefined
    }

    if (!config.task2DateField)
      throw Error(`${prefix}task2DateField missing forecast tasks in progress grouping`)

    const endValue = task2[config.task2DateField as keyof RecalcCompletionForecast]
    end = endValue == null ? new Date(Number.NaN) : new Date(endValue)
    if (Number.isNaN(end.getTime())) {
      reportForecastIntervalWarning(
        notificationService,
        'missingTask2End',
        task1Item.jobNumber,
        `${prefix}No end date for task 2 (${task2.taskTitle}) for job ${task1Item.jobNumber}${suffix}`,
        true
      )
      return undefined
    }

    if (start > end) {
      reportForecastIntervalWarning(
        notificationService,
        'startAfterEnd',
        task1Item.jobNumber,
        `${prefix}Date (${start}) for task 1 (${task1Item.taskTitle}) is after date (${end}) for task 2 (${task2.taskTitle}) for job ${task1Item.jobNumber}${suffix}`,
        false
      )
      return undefined
    }
  }

  // clip dates to the selected date range filter (if applicable)
  let rangeStart: Date | undefined
  let rangeEnd: Date | undefined
  if (config.dateRangeTypeId === DateRangeType['Custom']) {
    if (config.dateFrom) rangeStart = new Date(config.dateFrom)
    if (config.dateTo) rangeEnd = new Date(config.dateTo)
  } else if (
    config.dateRangeTypeId != null &&
    DateUtilsService.isPresetDateRange(config.dateRangeTypeId)
  ) {
    const [presetStart, presetEnd] = DateUtilsService.getGroupingStartAndEndDate(
      config.dateRangeTypeId,
      config.includeCurrentPartialPeriod,
      config.dateAxisTypeId,
      config.argumentFieldIsDate
    )
    rangeStart = presetStart.toDate()
    rangeEnd = presetEnd.toDate()
  }

  if (rangeStart && start < rangeStart) {
    start = rangeStart
  }

  if (rangeEnd && end > rangeEnd) {
    end = rangeEnd
  }

  // No overlap with the selected date-range window.
  if (start > end) {
    return undefined
  }

  return [start, end]
}

export function getDistinctOverlappingForecastTasks(
  task1Items: RecalcCompletionForecast[],
  task2Items: RecalcCompletionForecast[],
  config: ChartConfig,
  notificationService: NotificationService
): RecalcCompletionForecast[] {
  const warningCollector = new ForecastIntervalWarningCollector(
    notificationService,
    config.cardTitle
  )
  const overlappingItems = task1Items.filter((item) =>
    getForecastTaskInProgressInterval(task2Items, item, config, warningCollector)
  )
  warningCollector.flush()

  return Array.from(
    new Map(overlappingItems.map((item) => [item.jobNumber, item])).values()
  )
}

export const FORECAST_TASK_IN_PROGRESS_GROUP_KEY_FUNC: GROUP_KEYS_FUNCTION<
  RecalcCompletionForecast
> = (
  task2Items: RecalcCompletionForecast[],
  task1Item: RecalcCompletionForecast,
  config: ChartConfig,
  notificationService: WarningNotifier
) => {
  const interval = getForecastTaskInProgressInterval(
    task2Items,
    task1Item,
    config,
    notificationService
  )

  return interval
    ? DateUtilsService.getPeriodKeysBetween(interval[0], interval[1], config.dateAxisTypeId)
    : []
}

export const FORECAST_TASKS_IN_PROGRESS_SERIES_GROUP_FIELDS: ChartField[] =
  FORECAST_TASKS_IN_PROGRESS_FILTER_FIELDS.filter(
    (jf) => jf.dataType === 'string' || jf.dataType === 'boolean'
  )
